import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase/client';
import { LogOut, AlertTriangle, CheckCircle, Camera, ArrowLeft, Check, X, Bell, BellOff, Mail } from 'lucide-react';
import { uploadAvatar, deleteAvatar } from '../services/supabase/storage';
import { useAvatarUrl } from '../hooks/useAvatarUrl';
import { validateImageFile } from '../utils/imageCompression';
import { createPortalSession, createCheckoutSession } from '../services/stripe/checkout';
import { startProTrial } from '../services/supabase/subscriptions';
import { subscribeUser, unsubscribeUser, isSubscribed, getPermissionStatus } from '../services/onesignal';
import Layout from '../components/layout/Layout';
import UpgradeModal from '../components/ui/UpgradeModal';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export default function Settings() {
  const navigate = useNavigate();
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const avatarInputRef = useRef(null);

  // Avatar
  const { url: avatarUrl, loading: avatarLoading, refresh: refreshAvatar } = useAvatarUrl(user?.id);

  // Profile update state
  const [fullName, setFullName] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletePasswordModal, setShowDeletePasswordModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  // Notifications state
  const [pushEnabled, setPushEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushLoading, setPushLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Load notification preferences from profile
  useEffect(() => {
    if (profile) {
      setPushEnabled(profile.notifications_push_enabled ?? true);
      setEmailEnabled(profile.notifications_email_enabled ?? true);
    }
  }, [profile]);

  // Check OneSignal subscription status on mount
  useEffect(() => {
    const checkNotificationStatus = async () => {
      const subscribed = await isSubscribed();
      const permission = await getPermissionStatus();
      setNotificationPermission(permission);

      // Sync OneSignal status with DB preference
      if (profile?.notifications_push_enabled && !subscribed && permission === 'granted') {
        // User wants push but not subscribed - auto-subscribe
        await subscribeUser(user.id);
      }
    };
    if (user && profile) {
      checkNotificationStatus();
    }
  }, [user, profile]);

  useEffect(() => {
    if (profile?.full_name) {
      setFullName(profile.full_name);
    } else {
      setFullName('');
    }
  }, [profile]);

  const getInitials = () => {
    if (fullName) {
      const names = fullName.split(' ');
      return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setMessage({ type: 'error', text: validation.error });
      return;
    }

    setUploadingAvatar(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await uploadAvatar(file, user.id);
      if (result.error) throw new Error(result.error);

      setMessage({ type: 'success', text: 'Zdjęcie profilowe zaktualizowane!' });
      setTimeout(() => refreshAvatar(), 500);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas uploadu zdjęcia' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    setUploadingAvatar(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await deleteAvatar(user.id);
      if (result.error) throw new Error(result.error);

      setMessage({ type: 'success', text: 'Zdjęcie profilowe usunięte' });
      setTimeout(() => refreshAvatar(), 500);
    } catch (error) {
      console.error('Error deleting avatar:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas usuwania zdjęcia' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', user.id);

      if (profileError) throw profileError;

      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: fullName }
      });

      if (authError) console.warn('Auth metadata update failed:', authError);

      await refreshProfile();
      setMessage({ type: 'success', text: 'Profil zaktualizowany pomyślnie!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas aktualizacji profilu' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Nowe hasła nie są identyczne' });
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Nowe hasło musi mieć minimum 6 znaków' });
      setLoading(false);
      return;
    }

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) throw new Error('Nieprawidłowe obecne hasło');

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setMessage({ type: 'success', text: 'Hasło zmienione pomyślnie!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas zmiany hasła' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: deletePassword,
      });

      if (signInError) throw new Error('Nieprawidłowe hasło');

      const { error: deleteError } = await supabase.rpc('delete_user_account', {
        user_id: user.id
      });

      if (deleteError) {
        console.warn('RPC not available, using alternative deletion method');
        const { error: profileDeleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', user.id);

        if (profileDeleteError) throw profileDeleteError;
      }

      await signOut();
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas usuwania konta' });
      setLoading(false);
      setShowDeletePasswordModal(false);
    }
  };

  const handleTogglePush = async () => {
    setPushLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const newValue = !pushEnabled;

      // Update database
      const { error } = await supabase
        .from('profiles')
        .update({ notifications_push_enabled: newValue })
        .eq('id', user.id);

      if (error) throw error;

      // Update OneSignal subscription
      if (newValue) {
        const success = await subscribeUser(user.id);
        if (!success) {
          const permission = await getPermissionStatus();
          if (permission === 'denied') {
            throw new Error('Powiadomienia zablokowane w przeglądarce. Odblokuj je w ustawieniach.');
          }
        }
      } else {
        await unsubscribeUser();
      }

      setPushEnabled(newValue);
      await refreshProfile();
      setMessage({
        type: 'success',
        text: newValue ? 'Powiadomienia push włączone' : 'Powiadomienia push wyłączone'
      });
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd przy zarządzaniu powiadomieniami push' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleToggleEmail = async () => {
    setEmailLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const newValue = !emailEnabled;

      // Update database
      const { error } = await supabase
        .from('profiles')
        .update({ notifications_email_enabled: newValue })
        .eq('id', user.id);

      if (error) throw error;

      setEmailEnabled(newValue);
      await refreshProfile();
      setMessage({
        type: 'success',
        text: newValue ? 'Powiadomienia email włączone' : 'Powiadomienia email wyłączone'
      });
    } catch (error) {
      console.error('Error toggling email notifications:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd przy zarządzaniu powiadomieniami email' });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await startProTrial();
      if (result.success) {
        await refreshProfile();
        setMessage({ type: 'success', text: 'Gratulacje! Rozpocząłeś 14-dniowy okres próbny planu Pro.' });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas rozpoczynania okresu próbnego' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePaid = async (billingPeriod = 'monthly') => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await createCheckoutSession(billingPeriod);
      if (!result.success) {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas przekierowania do płatności' });
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await createPortalSession();
      if (!result.success) {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error opening subscription portal:', error);
      setMessage({ type: 'error', text: error.message || 'Błąd podczas otwierania portalu subskrypcji' });
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Error logging out:', error);
      setMessage({ type: 'error', text: 'Błąd podczas wylogowania' });
      setIsLoggingOut(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Back Navigation */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Powrót do panelu</span>
        </button>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-8 p-4 rounded-xl flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200'
              : 'bg-rose-50 border border-rose-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            )}
            <p className={`text-sm ${message.type === 'success' ? 'text-emerald-800' : 'text-rose-800'}`}>
              {message.text}
            </p>
          </div>
        )}

        {/* One large white card containing all sections */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          {/* Section 1: Profile Information */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Informacje o profilu</h2>

            {/* Avatar Section - Framed */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-4">
                Zdjęcie profilowe
              </label>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white text-xl font-semibold">
                    {avatarLoading || uploadingAvatar ? (
                      <div className="animate-pulse">...</div>
                    ) : avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span>{getInitials()}</span>
                    )}
                  </div>
                  {uploadingAvatar && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Camera className="w-4 h-4" />
                    {avatarUrl ? 'Zmień zdjęcie' : 'Dodaj zdjęcie'}
                  </button>
                  {avatarUrl && (
                    <button
                      type="button"
                      onClick={handleAvatarDelete}
                      disabled={uploadingAvatar}
                      className="flex items-center gap-2 px-4 py-2 text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      <X className="w-4 h-4" />
                      Usuń zdjęcie
                    </button>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    JPG, PNG lub WEBP. Maksymalnie 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Profile Form - Framed */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                />
                <p className="text-xs text-slate-500 mt-1">Nie można zmienić adresu email</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Imię i nazwisko
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
                  placeholder="Wprowadź imię i nazwisko"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Zapisywanie...' : 'Zapisz zmiany'}
              </button>
              </form>
            </div>
          </section>

          {/* Section 2: Notifications */}
          <section className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Powiadomienia</h2>

            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <p className="text-sm text-slate-600 mb-6">
                Otrzymuj powiadomienia o ważnych wydarzeniach: nadchodzące inspekcje, koniec okresu próbnego, problemy z płatnością.
              </p>

              {/* Push Notifications Toggle - HIDDEN temporarily */}
              {false && (
              <div className="flex items-center justify-between py-4 border-b border-slate-200">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Bell className={`w-5 h-5 ${pushEnabled ? 'text-violet-600' : 'text-slate-400'}`} />
                    <h3 className="font-medium text-slate-900">Powiadomienia push</h3>
                  </div>
                  <p className="text-sm text-slate-600 ml-8">
                    Powiadomienia w przeglądarce i na telefonie
                  </p>
                </div>

                <button
                  onClick={handleTogglePush}
                  disabled={pushLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    pushEnabled ? 'bg-violet-600' : 'bg-slate-300'
                  }`}
                  role="switch"
                  aria-checked={pushEnabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      pushEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              )}

              {false && notificationPermission === 'denied' && pushEnabled && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-900">
                    ⚠️ Powiadomienia push zablokowane w przeglądarce. Odblokuj je w ustawieniach, aby otrzymywać alerty.
                  </p>
                </div>
              )}

              {/* Email Notifications Toggle */}
              <div className="flex items-center justify-between py-4 gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Mail className={`w-5 h-5 flex-shrink-0 ${emailEnabled ? 'text-violet-600' : 'text-slate-400'}`} />
                    <h3 className="font-medium text-slate-900">Powiadomienia email</h3>
                  </div>
                  <p className="text-sm text-slate-600 ml-8 truncate">
                    Email: {user?.email}
                  </p>
                </div>

                <button
                  onClick={handleToggleEmail}
                  disabled={emailLoading}
                  className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    emailEnabled ? 'bg-violet-600' : 'bg-slate-300'
                  }`}
                  role="switch"
                  aria-checked={emailEnabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </section>

          {/* Section 3: Change Password */}
          <section className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Zmień hasło</h2>

            {/* Password Form - Framed */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <form onSubmit={handleChangePassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Obecne hasło
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-shadow"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nowe hasło
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-shadow"
                  required
                  minLength={6}
                />
                <p className="text-xs text-slate-500 mt-1">Minimum 6 znaków</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Potwierdź nowe hasło
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-shadow"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Zmienianie...' : 'Zmień hasło'}
              </button>
              </form>
            </div>
          </section>

          {/* Section 4: Subscription Management */}
          <section className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Subskrypcja</h2>

            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 mb-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900">
                    {profile?.subscription_plan === 'pro' ? 'Plan Pro'
                      : profile?.subscription_plan === 'starter' ? 'Plan Starter'
                      : 'Plan Darmowy'}
                  </h3>
                  {profile?.subscription_plan === 'starter' && profile?.stripe_subscription_id && (
                    <p className="text-lg text-slate-600 mt-1">
                      29 PLN / miesiąc
                    </p>
                  )}
                  {profile?.subscription_plan === 'pro' && profile?.stripe_subscription_id && (
                    <p className="text-lg text-slate-600 mt-1">
                      {profile?.subscription_interval === 'year' ? (
                        <>490 PLN / rok <span className="text-sm text-slate-500">(41 PLN/mc)</span></>
                      ) : (
                        '49 PLN / miesiąc'
                      )}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Status:</span>
                  {(profile?.subscription_plan === 'starter' || profile?.subscription_plan === 'pro') && !profile?.cancel_at ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200">
                      <Check className="w-3.5 h-3.5" />
                      Aktywny
                    </span>
                  ) : profile?.cancel_at ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-medium border border-orange-200">
                      Anulowana
                    </span>
                  ) : (
                    <span className="text-sm text-slate-600 font-medium">Darmowy</span>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Nieruchomości</span>
                    <span className="font-medium text-slate-900">
                      {profile?.properties_count || 0} / {
                        profile?.subscription_plan === 'pro' ? '5'
                          : profile?.subscription_plan === 'starter' ? '1'
                          : '1'
                      }
                    </span>
                  </div>
                  {(profile?.subscription_plan === 'starter' || profile?.subscription_plan === 'pro') && profile?.subscription_status === 'trialing' && profile?.trial_ends_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Okres próbny kończy się</span>
                      <span className="font-medium text-orange-600">
                        {new Date(profile.trial_ends_at).toLocaleDateString('pl-PL')}
                      </span>
                    </div>
                  )}
                  {(profile?.subscription_plan === 'starter' || profile?.subscription_plan === 'pro') && profile?.stripe_subscription_id && profile?.cancel_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subskrypcja wygasa</span>
                      <span className="font-medium text-rose-600">
                        {new Date(profile.cancel_at).toLocaleDateString('pl-PL')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Starter with paid subscription */}
            {profile?.subscription_plan === 'starter' && profile?.stripe_subscription_id ? (
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleManageSubscription}
                  disabled={loading}
                  className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? 'Otwieranie...' : 'Zarządzaj subskrypcją'}
                </button>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={loading}
                  className="bg-white text-violet-700 px-6 py-2.5 rounded-lg border-2 border-violet-600 hover:bg-violet-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Upgrade do Pro (5 nieruchomości)
                </button>
              </div>
            /* Starter in trial */
            ) : profile?.subscription_plan === 'starter' && !profile?.stripe_subscription_id ? (
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Korzystasz z okresu próbnego Starter. Dodaj metodę płatności przed końcem okresu próbnego.
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={loading}
                  className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Przejdź do płatności
                </button>
              </div>
            /* Pro with paid subscription */
            ) : profile?.subscription_plan === 'pro' && profile?.stripe_subscription_id ? (
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Otwieranie...' : 'Zarządzaj subskrypcją'}
              </button>
            /* Pro in trial */
            ) : profile?.subscription_plan === 'pro' && !profile?.stripe_subscription_id ? (
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Korzystasz z okresu próbnego Pro. Dodaj metodę płatności przed końcem okresu próbnego.
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={loading}
                  className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Przejdź do płatności
                </button>
              </div>
            /* Free plan */
            ) : (
              <div className="flex gap-3">
                {!profile?.trial_started_at ? (
                  <>
                    <button
                      onClick={handleStartTrial}
                      disabled={loading}
                      className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {loading ? 'Rozpoczynanie...' : 'Rozpocznij okres próbny'}
                    </button>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      disabled={loading}
                      className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      Kup Plan
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    disabled={loading}
                    className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    Kup Plan
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Section 5: Logout */}
          <section className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Wyloguj się</h2>

            {/* Logout - Framed */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <p className="text-slate-600 mb-4 text-sm">
                Zakończ sesję w aplikacji. Będziesz mógł zalogować się ponownie w dowolnym momencie.
              </p>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors text-sm border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? (
                  <>
                    <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Wylogowywanie...</span>
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4" />
                    <span>Wyloguj</span>
                  </>
                )}
              </button>
            </div>
          </section>

          {/* Section 5: Danger Zone */}
          <section className="mt-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Usuwanie konta</h2>

              <div className="mb-6">
                <p className="text-slate-700 mb-3">
                  Usunięcie konta jest operacją nieodwracalną. Spowoduje trwałe usunięcie:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm">
                  <li>Wszystkich nieruchomości</li>
                  <li>Wszystkich inspekcji i zdjęć</li>
                  <li>Historii konta i ustawień</li>
                </ul>
              </div>

              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-rose-600 text-white px-6 py-2.5 rounded-lg hover:bg-rose-700 transition-colors font-medium"
              >
                Usuń konto
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        title="Potwierdź usunięcie konta"
        message="Ta operacja jest nieodwracalna. Wprowadź hasło, aby potwierdzić usunięcie konta i wszystkich danych."
        confirmText="Kontynuuj"
        cancelText="Anuluj"
        variant="danger"
        onConfirm={() => {
          setShowDeleteModal(false);
          setShowDeletePasswordModal(true);
        }}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Delete Password Modal */}
      {showDeletePasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => {
                  setShowDeletePasswordModal(false);
                  setDeletePassword('');
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <h3 className="text-xl font-semibold text-slate-900 mb-4 text-center">Wprowadź hasło</h3>
            <p className="text-slate-600 mb-6 text-sm text-center">
              Aby potwierdzić usunięcie konta, wprowadź swoje hasło.
            </p>

            <form onSubmit={handleDeleteAccount}>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent mb-6"
                placeholder="Twoje hasło"
                required
                autoFocus
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeletePasswordModal(false);
                    setDeletePassword('');
                  }}
                  className="flex-1 bg-slate-100 text-slate-700 px-4 py-2.5 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                  disabled={loading}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-rose-600 text-white px-4 py-2.5 rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? 'Usuwanie...' : 'Usuń konto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Subskrypcja Pro"
        message="Wybierz plan subskrypcji Pro i odblokuj pełen potencjał PropertyCheck.PRO"
        profile={profile}
        user={user}
        refreshProfile={refreshProfile}
        onStartTrial={handleStartTrial}
        onUpgradePaid={handleUpgradePaid}
      />
    </Layout>
  );
}
